import { type Href, useRouter } from 'expo-router';
import { Pressable, ScrollView, Text, View } from 'react-native';
import { WebHead } from '../components';

const HOME_ROUTE = '/' as Href;
const SUPPORT_ROUTE = '/support' as Href;
const MCP_SERVER_URL = 'https://api.perdiem.app/mcp';

const TOOLS = [
  {
    name: 'Financial summary',
    description:
      'Summarizes net worth, account balances, holdings, current-year cash flow, Per Diem, FI year, and FI age.',
  },
  {
    name: 'Transaction search',
    description:
      'Finds existing transactions by date, merchant, category, or category group, including recent spending details.',
  },
  {
    name: 'Budget versus actual',
    description:
      'Compares planned budget amounts with actual spending for month, quarter, year-to-date, or trailing-year views.',
  },
  {
    name: 'FIRE plan analysis',
    description:
      'Explains FIRE timing, FI year, FI age, projection context, success-rate trends, and simulation methodology.',
  },
];

const EXAMPLE_PROMPTS = [
  'Summarize my net worth, top holdings, and current-year cash flow from Per Diem.',
  'Can we afford a Mexico trip next winter based on our travel budget?',
  'Find my recent restaurant transactions and summarize where the money went.',
  'Which budget categories are most over or under plan this year?',
  'Explain my projected FI year, FI age, and the assumptions behind my FIRE plan.',
];

const TROUBLESHOOTING = [
  {
    title: 'The connector asks me to sign in again.',
    body: 'Reconnect through the assistant connector settings and approve read-only access in the Per Diem browser window.',
  },
  {
    title: 'The answer looks incomplete.',
    body: 'Per Diem can only return data already available in your account. Complete onboarding and add or connect accounts, income, budgets, holdings, and FIRE plan details for better answers.',
  },
  {
    title: 'The assistant did not use Per Diem.',
    body: 'Ask directly with "Use Per Diem..." when you want ChatGPT, Claude, Grok, or another supported assistant to ground the answer in your Per Diem data.',
  },
  {
    title: 'I want to revoke access.',
    body: 'Open Integrations in Per Diem to revoke assistant access, or remove Per Diem from the assistant connector settings. You can reconnect later.',
  },
];

export default function AiConnectorsPage() {
  const router = useRouter();

  return (
    <>
      <WebHead>
        <title>Per Diem AI Connector Documentation</title>
        <meta
          name="description"
          content="Learn how to connect Per Diem to ChatGPT, Claude, Grok, and other MCP clients, including setup, supported tools, privacy, troubleshooting, and support."
        />
      </WebHead>
      <ScrollView className="flex-1 bg-stone-950" contentContainerStyle={{ paddingBottom: 56 }}>
        <View className="mx-auto w-full max-w-5xl px-6 pb-10 pt-6">
          <View className="mb-10 flex-row items-center justify-between">
            <Pressable onPress={() => router.push(HOME_ROUTE)}>
              <Text className="text-sm font-semibold uppercase tracking-[0.28em] text-emerald-300">
                Per Diem
              </Text>
            </Pressable>
            <Pressable
              onPress={() => router.push(SUPPORT_ROUTE)}
              className="rounded-full border border-stone-700 px-4 py-2"
            >
              <Text className="text-sm font-semibold text-stone-100">Support</Text>
            </Pressable>
          </View>

          <View className="border-b border-stone-800 pb-10">
            <Text className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-300">
              AI Connectors
            </Text>
            <Text className="mt-4 max-w-4xl text-4xl font-semibold leading-[48px] text-stone-50">
              Connect Per Diem to your AI assistant.
            </Text>
            <Text className="mt-5 max-w-3xl text-base leading-7 text-stone-300">
              The Per Diem MCP server gives supported assistants such as ChatGPT, Claude, and Grok
              read-only access to your Per Diem financial data after you sign in and approve the
              connection. Use it to analyze budgets, transactions, balances, holdings,
              current-year cash flow, and FIRE projections.
            </Text>
          </View>

          <View className="gap-10 py-10">
            <View>
              <Text className="text-2xl font-semibold text-stone-50">Connection requirements</Text>
              <Text className="mt-3 text-base leading-7 text-stone-300">
                You need a Per Diem account. For useful results, complete onboarding and add or
                connect the data you want the assistant to analyze, such as accounts, transactions,
                income, budgets, holdings, assets, debts, and FIRE plan details.
              </Text>
            </View>

            <View>
              <Text className="text-2xl font-semibold text-stone-50">How to connect</Text>
              <View className="mt-4 gap-3">
                {[
                  'Open the connector or app settings in your AI assistant.',
                  `If Per Diem is listed, choose it. If not, choose a custom connector and use ${MCP_SERVER_URL}.`,
                  'Sign in to Per Diem in the browser window that opens.',
                  'Review the consent screen and approve read-only access.',
                  'Ask your assistant to use Per Diem for finance questions.',
                ].map((step, index) => (
                  <View key={step} className="flex-row gap-3">
                    <Text className="text-base font-semibold text-emerald-300">{index + 1}.</Text>
                    <Text className="flex-1 text-base leading-7 text-stone-300">{step}</Text>
                  </View>
                ))}
              </View>
            </View>

            <View>
              <Text className="text-2xl font-semibold text-stone-50">Grok setup</Text>
              <Text className="mt-3 text-base leading-7 text-stone-300">
                Grok supports custom MCP connectors. Per Diem is not listed in Grok's connector
                directory yet, but you can add it manually.
              </Text>
              <View className="mt-4 gap-3">
                {[
                  'In Grok, click Connect or open connector settings.',
                  'Choose New Connector, then Custom.',
                  'Name the connector Per Diem.',
                  `Set Server URL to ${MCP_SERVER_URL}.`,
                  'Click Add Connector and approve read-only access in the Per Diem browser window.',
                ].map((step, index) => (
                  <View key={step} className="flex-row gap-3">
                    <Text className="text-base font-semibold text-emerald-300">{index + 1}.</Text>
                    <Text className="flex-1 text-base leading-7 text-stone-300">{step}</Text>
                  </View>
                ))}
              </View>
            </View>

            <View>
              <Text className="text-2xl font-semibold text-stone-50">What the connector can do</Text>
              <View className="mt-4 gap-4">
                {TOOLS.map((tool) => (
                  <View key={tool.name} className="border-l-2 border-emerald-400 pl-4">
                    <Text className="text-lg font-semibold text-stone-100">{tool.name}</Text>
                    <Text className="mt-1 text-base leading-7 text-stone-300">
                      {tool.description}
                    </Text>
                  </View>
                ))}
              </View>
            </View>

            <View>
              <Text className="text-2xl font-semibold text-stone-50">Example prompts</Text>
              <View className="mt-4 gap-3">
                {EXAMPLE_PROMPTS.map((prompt) => (
                  <Text key={prompt} className="text-base leading-7 text-stone-300">
                    "{prompt}"
                  </Text>
                ))}
              </View>
            </View>

            <View>
              <Text className="text-2xl font-semibold text-stone-50">Data handling</Text>
              <Text className="mt-3 text-base leading-7 text-stone-300">
                The connector is read-only. It cannot move money, execute trades, change budgets,
                edit transactions, create scenarios, delete records, or trigger recalculations. It
                only returns Per Diem data requested by the user through the connected assistant, and
                data is transmitted over HTTPS/TLS.
              </Text>
              <View className="mt-4 flex-row flex-wrap gap-3">
                {[
                  ['Privacy policy', '/privacy-policy'],
                  ['Terms of service', '/terms-of-service'],
                  ['Security policy', '/security-policy'],
                ].map(([label, route]) => (
                  <Pressable
                    key={label}
                    onPress={() => router.push(route as Href)}
                    className="rounded-full border border-stone-700 px-4 py-2"
                  >
                    <Text className="text-sm font-semibold text-stone-100">{label}</Text>
                  </Pressable>
                ))}
              </View>
            </View>

            <View>
              <Text className="text-2xl font-semibold text-stone-50">Troubleshooting</Text>
              <View className="mt-4 gap-4">
                {TROUBLESHOOTING.map((item) => (
                  <View key={item.title}>
                    <Text className="text-lg font-semibold text-stone-100">{item.title}</Text>
                    <Text className="mt-1 text-base leading-7 text-stone-300">{item.body}</Text>
                  </View>
                ))}
              </View>
            </View>

            <View className="border-t border-stone-800 pt-8">
              <Text className="text-2xl font-semibold text-stone-50">Support</Text>
              <Text className="mt-3 text-base leading-7 text-stone-300">
                If you need help connecting Per Diem, include your Per Diem account email, the
                assistant you are using, the prompt you tried, and any visible error message.
              </Text>
              <Pressable
                onPress={() => router.push(SUPPORT_ROUTE)}
                className="mt-5 self-start rounded-full bg-emerald-500 px-5 py-3"
              >
                <Text className="text-sm font-semibold text-stone-950">Contact support</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </ScrollView>
    </>
  );
}
